import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import PaginationButtons from '../../../components/Common/Pagination';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import {
  BULK_RESEND_BATCH_SIZE,
  MarketingAuditFilters,
  MarketingAuditRecord,
  ResentStatusFilter,
  bulkResendMarketingAudits,
  fetchMarketingAuditIds,
  fetchMarketingAudits,
  resendMarketingAudit,
} from './services/MarketingAudit.service';
import { Toast } from '../../../components/Common/Toast';
import { AdminNavHandler } from '../adminNav';

type Option = { value: string; label: string };
type ResendStatus = 'idle' | 'loading' | 'success' | 'error';

const EVENT_TYPE_OPTIONS: Option[] = [
  { value: '', label: 'All Event Types' },
  { value: 'signup', label: 'Signup' },
  { value: 'user_login', label: 'User Login' },
  { value: 'use_case_selected', label: 'Use Case Selected' },
  { value: 'classroom_info', label: 'Classroom Info' },
  { value: 'classroom_created', label: 'Classroom Created' },
  { value: 'onboarding_complete', label: 'Onboarding Complete' },
  { value: 'onboarding_video', label: 'Onboarding Video' },
  { value: 'start_tour', label: 'Start Tour' },
  { value: 'finish_tour', label: 'Finish Tour' },
  { value: 'onboarding_survey_complete', label: 'Survey Complete' },
  { value: 'onboarding_survey_skipped', label: 'Survey Skipped' },
  { value: 'book_demo_completed', label: 'Book Demo Completed' },
  { value: 'class_students_threshold', label: 'Class Students Threshold' },
  { value: 'teacher_books_threshold', label: 'Teacher Books Threshold' },
  { value: 'student_books_threshold', label: 'Student Books Threshold' },
  { value: 'pre_session_flyer', label: 'Pre Session Flyer' },
  { value: 'parent_acknowledgement', label: 'Parent Acknowledgement' },
  { value: 'post_session_flyer', label: 'Post Session Flyer' },
  { value: 'book_viewed', label: 'Book Viewed' },
  { value: 'purchase_made', label: 'Purchase Made' },
  { value: 'teacher_shipped', label: 'Teacher Shipped' },
  { value: 'project_created', label: 'Project Created' },
  { value: 'print_passwords', label: 'Print Passwords' },
  { value: 'school_details', label: 'School Details' },
  { value: 'program_details', label: 'Program Details' },
  { value: 'referral_payment_type', label: 'Referral Payment Type' },
  { value: 'start_date', label: 'Start Date' },
  { value: 'start_date_settings', label: 'Start Date Settings' },
  { value: 'students_added', label: 'Students Added' },
  { value: 'students_added_onboarding', label: 'Students Added (Onboarding)' },
];

const RESENT_STATUS_OPTIONS: Option[] = [
  { value: '', label: 'All Resend Status' },
  { value: 'not_resent', label: 'Not Resent' },
  { value: 'resent', label: 'Resent' },
];

const getEventBadgeClass = (eventType: string): string => {
  if (['signup', 'user_login'].includes(eventType)) return 'bg-green-100 text-green-700';
  if (
    eventType.startsWith('onboarding') ||
    ['start_tour', 'finish_tour', 'use_case_selected', 'book_demo_completed'].includes(eventType)
  )
    return 'bg-blue-100 text-blue-700';
  if (
    ['school_details', 'program_details', 'referral_payment_type', 'start_date', 'start_date_settings'].includes(
      eventType
    )
  )
    return 'bg-purple-100 text-purple-700';
  if (
    [
      'classroom_created',
      'classroom_info',
      'project_created',
      'students_added',
      'students_added_onboarding',
      'class_students_threshold',
      'print_passwords',
    ].includes(eventType)
  )
    return 'bg-indigo-100 text-indigo-700';
  if (['book_viewed', 'teacher_books_threshold', 'student_books_threshold'].includes(eventType))
    return 'bg-yellow-100 text-yellow-700';
  if (['purchase_made', 'teacher_shipped'].includes(eventType)) return 'bg-orange-100 text-orange-700';
  if (['pre_session_flyer', 'parent_acknowledgement', 'post_session_flyer'].includes(eventType))
    return 'bg-pink-100 text-pink-700';
  return 'bg-gray-100 text-gray-600';
};

const formatLabel = (str: string) => str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const PAGE_SIZE = 25;

interface MarketingAuditProps {
  onOptionClick?: AdminNavHandler;
  initialEmail?: string;
}

const MarketingAudit = ({ onOptionClick, initialEmail }: MarketingAuditProps) => {
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState<MarketingAuditRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [emailFilter, setEmailFilter] = useState(initialEmail || '');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [resentStatusFilter, setResentStatusFilter] = useState<ResentStatusFilter>('');
  const [resendStatus, setResendStatus] = useState<Record<string, ResendStatus>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) navigate('/admin');
  });

  const filters: MarketingAuditFilters = useMemo(
    () => ({
      email: emailFilter || null,
      eventType: eventTypeFilter || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
      resentStatus: resentStatusFilter || null,
    }),
    [emailFilter, eventTypeFilter, fromDate, toDate, resentStatusFilter]
  );

  const loadAudits = async (page: number, f: MarketingAuditFilters) => {
    setLoading(true);
    try {
      const result = await fetchMarketingAudits(page, f, PAGE_SIZE);
      setAudits(result.audits);
      setTotalPages(result.totalPages);
      setTotalRecords(result.totalRecords);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudits(currentPage, filters);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadAudits(1, filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [emailFilter, eventTypeFilter, fromDate, toDate, resentStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value: string) => {
    setEmailFilter(value);
  };

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages) setCurrentPage(+page);
  };

  const handleClearFilters = () => {
    setEmailFilter('');
    setEventTypeFilter('');
    setFromDate('');
    setToDate('');
    setResentStatusFilter('');
  };

  const updateRowResent = (id: string, resentAt: string) => {
    setAudits((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, resent_at: resentAt, resent_count: (a.resent_count || 0) + 1 } : a
      )
    );
  };

  const handleResend = async (id: string) => {
    setResendStatus((prev) => ({ ...prev, [id]: 'loading' }));
    const result = await resendMarketingAudit(id);
    const next: ResendStatus = result.success ? 'success' : 'error';
    setResendStatus((prev) => ({ ...prev, [id]: next }));
    if (next === 'success') {
      if (result.resent_at) updateRowResent(id, result.resent_at);
      setTimeout(() => {
        setResendStatus((prev) => ({ ...prev, [id]: 'idle' }));
        // Refetch so the row's resent timestamp reflects what's actually persisted.
        loadAudits(currentPage, filters);
      }, 1500);
    } else {
      Toast({ title: 'Webhook resend failed', message: result.error || 'The marketing API did not accept the request.' }, true);
    }
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = audits.length > 0 && audits.every((a) => selectedIds.has(a.id));

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        audits.forEach((a) => next.delete(a.id));
      } else {
        audits.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };

  const handleSelectAllMatching = async () => {
    try {
      const ids = await fetchMarketingAuditIds(filters);
      if (ids.length === 0) {
        Toast('No records match the current filters', true);
        return;
      }
      setSelectedIds(new Set(ids));
      Toast(`Selected ${ids.length.toLocaleString()} records matching filters`, false);
    } catch {
      // Toast already shown by service
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  // Bulk resend: client orchestrates strict-sequential server batches of BULK_RESEND_BATCH_SIZE,
  // pre-sorted oldest -> newest. Server enforces the batch cap and time budget.
  const handleBulkResend = async () => {
    if (selectedIds.size === 0 || bulkRunning) return;

    // Show loading immediately; ordering resolution may take a moment for large selections.
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: selectedIds.size, failed: 0 });

    // Order the selection oldest -> newest. Fast path: every selected id is on the current
    // page, sort using the in-memory `last_active`. Slow path: fall back to the server /ids
    // endpoint to fetch authoritative ordering.
    let orderedIds: string[];
    const auditsById = new Map(audits.map((a) => [a.id, a]));
    const allInState = Array.from(selectedIds).every((id) => auditsById.has(id));
    if (allInState) {
      orderedIds = Array.from(selectedIds).sort((a, b) => {
        const ta = auditsById.get(a)?.last_active || '';
        const tb = auditsById.get(b)?.last_active || '';
        return ta < tb ? -1 : ta > tb ? 1 : 0;
      });
    } else {
      try {
        const allMatching = await fetchMarketingAuditIds(filters); // oldest -> newest
        const selSet = selectedIds;
        const filtered = allMatching.filter((id) => selSet.has(id));
        const known = new Set(filtered);
        const leftovers = Array.from(selSet).filter((id) => !known.has(id));
        orderedIds = [...filtered, ...leftovers];
      } catch {
        orderedIds = Array.from(selectedIds);
      }
    }

    setBulkProgress({ done: 0, total: orderedIds.length, failed: 0 });

    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < orderedIds.length; i += BULK_RESEND_BATCH_SIZE) {
      const chunk = orderedIds.slice(i, i + BULK_RESEND_BATCH_SIZE);
      const results = await bulkResendMarketingAudits(chunk);

      // Index results by id so we can detect missing ones (server error → empty/partial results).
      const resultsById = new Map(results.map((r) => [r.id, r]));
      for (const id of chunk) {
        const r = resultsById.get(id);
        if (r?.success && r.resent_at) {
          updateRowResent(id, r.resent_at);
          succeeded += 1;
        } else {
          failed += 1;
          if (r?.error) console.error('Bulk resend failed for', id, r.error);
          else console.error('Bulk resend: no result returned for', id);
        }
      }
      setBulkProgress({ done: succeeded + failed, total: orderedIds.length, failed });
    }

    // Refetch the current page so the visible "Resent" timestamps reflect what's actually
    // persisted on the server, not just the optimistic in-memory updates.
    await loadAudits(currentPage, filters);

    setBulkRunning(false);
    if (failed === 0) {
      Toast(`Resent ${succeeded} records`, false);
    } else if (succeeded === 0) {
      Toast(
        {
          title: 'Bulk resend failed',
          message: `0 of ${orderedIds.length} sent. Check the server logs and webhook configuration.`,
        },
        true
      );
    } else {
      Toast(
        {
          title: 'Bulk resend completed with errors',
          message: `${succeeded} sent, ${failed} failed. Re-run with "Not Resent" filter to retry.`,
        },
        true
      );
    }
    setSelectedIds(new Set());

    // Auto-dismiss the progress bar after a few seconds once the run completes.
    setTimeout(() => setBulkProgress(null), 4000);
  };

  const hasActiveFilters = emailFilter || eventTypeFilter || fromDate || toDate || resentStatusFilter;
  const selectionCount = selectedIds.size;

  return (
    <div className="p-0 md:p-4">
      {/* Toolbar */}
      <div className="flex justify-between items-center flex-wrap gap-2 my-5">
        <div className="flex items-center gap-2 flex-wrap">
          <AdminSearchInput
            placeholder="Search by email..."
            onSearch={handleSearchChange}
            defaultValue={initialEmail || ''}
            className="w-64"
          />
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={resentStatusFilter}
            onChange={(e) => setResentStatusFilter(e.target.value as ResentStatusFilter)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {RESENT_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-xs text-gray-500 hover:text-gray-700 underline px-1"
            >
              Clear filters
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {totalRecords > 0 && (
            <span className="text-xs text-gray-500">{totalRecords.toLocaleString()} records</span>
          )}
          <button
            onClick={() => loadAudits(currentPage, filters)}
            disabled={loading || bulkRunning}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:shadow-md transition-shadow text-sm border border-gray-300 disabled:opacity-50"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Selection / bulk action bar */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2 px-2 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-gray-700">
            <strong>{selectionCount.toLocaleString()}</strong> selected
          </span>
          <button
            onClick={handleSelectAllMatching}
            disabled={bulkRunning}
            className="text-blue-700 hover:text-blue-900 hover:underline disabled:opacity-50"
          >
            Select all matching filters
          </button>
          {selectionCount > 0 && (
            <button
              onClick={handleClearSelection}
              disabled={bulkRunning}
              className="text-gray-600 hover:text-gray-900 hover:underline disabled:opacity-50"
            >
              Clear selection
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {bulkProgress && (() => {
            const { done, total, failed } = bulkProgress;
            const succeeded = Math.max(0, done - failed);
            const succeededPct = total > 0 ? (succeeded / total) * 100 : 0;
            const failedPct = total > 0 ? (failed / total) * 100 : 0;
            return (
              <div className="flex items-center gap-2">
                <div
                  className="w-32 h-2 bg-blue-100 rounded-full overflow-hidden"
                  role="progressbar"
                  aria-valuenow={done}
                  aria-valuemin={0}
                  aria-valuemax={total}
                >
                  <div className="flex h-full">
                    <div className="h-full bg-green-500" style={{ width: `${succeededPct}%` }} />
                    <div className="h-full bg-red-500" style={{ width: `${failedPct}%` }} />
                  </div>
                </div>
                <span className="text-xs text-gray-700 whitespace-nowrap tabular-nums">
                  {bulkRunning ? `${done}/${total}` : `${succeeded}/${total} sent`}
                  {failed > 0 ? <span className="text-red-600">{` · ${failed} failed`}</span> : null}
                </span>
              </div>
            );
          })()}
          <button
            onClick={handleBulkResend}
            disabled={selectionCount === 0 || bulkRunning}
            className="px-3 py-1.5 bg-storyblue text-white rounded-md text-sm hover:opacity-90 disabled:opacity-40"
          >
            {bulkRunning ? 'Resending…' : `Resend selected (${selectionCount.toLocaleString()})`}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="w-full overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="py-2 text-left px-3 border-b text-sm w-8">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={togglePage}
                      disabled={audits.length === 0 || bulkRunning}
                      title="Select all on this page"
                    />
                  </th>
                  <th className="py-2 text-left px-4 border-b text-sm">Timestamp</th>
                  <th className="py-2 text-left px-4 border-b text-sm">Event Type</th>
                  <th className="py-2 text-left px-4 border-b text-sm">Email</th>
                  <th className="py-2 text-left px-4 border-b text-sm">Name</th>
                  <th className="py-2 text-left px-4 border-b text-sm" style={{ minWidth: '14rem' }}>
                    Data
                  </th>
                  <th className="py-2 text-left px-4 border-b text-sm">Student / Parent</th>
                  <th className="py-2 text-left px-4 border-b text-sm">Resent</th>
                  <th className="py-2 text-left px-4 border-b text-sm"></th>
                </tr>
              </thead>
              <tbody>
                {audits.length > 0 ? (
                  audits.map((audit, index) => {
                    const isSelected = selectedIds.has(audit.id);
                    return (
                      <tr
                        key={audit.id || index}
                        className={`${isSelected ? 'bg-blue-50' : index % 2 === 0 ? 'bg-gray-100' : ''}`}
                      >
                        {/* Checkbox */}
                        <td className="text-left py-2 px-3 border-b">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(audit.id)}
                            disabled={bulkRunning}
                          />
                        </td>

                        {/* Timestamp */}
                        <td className="text-left py-2 px-4 border-b text-xs whitespace-nowrap text-gray-500">
                          {formatDate(audit.last_active)}
                        </td>

                        {/* Event Type */}
                        <td className="text-left py-2 px-4 border-b">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getEventBadgeClass(audit.event_type)}`}
                          >
                            {formatLabel(audit.event_type)}
                          </span>
                        </td>

                        {/* Email */}
                        <td className="text-left py-2 px-4 border-b text-xs">
                          {audit.email && onOptionClick ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onOptionClick('Customers', { customerEmail: audit.email }); }}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                              title="View customer"
                            >
                              {audit.email}
                            </button>
                          ) : (
                            audit.email || '—'
                          )}
                        </td>

                        {/* Name */}
                        <td className="text-left py-2 px-4 border-b text-xs text-gray-700 whitespace-nowrap">
                          {[audit.first_name, audit.last_name].filter(Boolean).join(' ') || '—'}
                        </td>

                        {/* Data */}
                        <td
                          className="text-left py-2 px-4 border-b"
                          style={{ minWidth: '14rem', maxWidth: '22rem' }}
                        >
                          {audit.data && Object.keys(audit.data).length > 0 ? (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-gray-400 hover:text-gray-700 select-none">
                                {Object.keys(audit.data)
                                  .slice(0, 2)
                                  .map((k) => (
                                    <span key={k} className="mr-2 text-gray-600">
                                      <span className="font-medium">{k}:</span>{' '}
                                      {String(audit.data[k]).slice(0, 20)}
                                      {String(audit.data[k]).length > 20 ? '…' : ''}
                                    </span>
                                  ))}
                                {Object.keys(audit.data).length > 2 && (
                                  <span className="text-gray-400">+{Object.keys(audit.data).length - 2} more</span>
                                )}
                              </summary>
                              <pre className="whitespace-pre-wrap break-words font-mono mt-1 text-gray-700 bg-white rounded p-1 border border-gray-200">
                                {JSON.stringify(audit.data, null, 2)}
                              </pre>
                            </details>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Student / Parent */}
                        <td className="text-left py-2 px-4 border-b text-xs text-gray-600">
                          {audit.parent_email || audit.student_id || audit.teacher_email ? (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-gray-400 hover:text-gray-700 select-none">
                                {audit.parent_email
                                  ? `parent: ${audit.parent_email}`
                                  : audit.student_id
                                    ? `student: ${audit.student_id}`
                                    : `teacher: ${audit.teacher_email}`}
                              </summary>
                              <div className="mt-1 space-y-0.5">
                                {audit.teacher_email && (
                                  <div>
                                    <span className="text-gray-400">teacher:</span>{' '}
                                    {onOptionClick ? (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onOptionClick('Customers', { teacherEmail: audit.teacher_email! }); }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                        title="View customer"
                                      >
                                        {audit.teacher_email}
                                      </button>
                                    ) : (
                                      audit.teacher_email
                                    )}
                                  </div>
                                )}
                                {audit.parent_email && (
                                  <div>
                                    <span className="text-gray-400">parent:</span>{' '}
                                    {[audit.parent_first_name, audit.parent_last_name].filter(Boolean).join(' ')}{' '}
                                    &lt;
                                    {onOptionClick ? (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onOptionClick('Customers', { customerEmail: audit.parent_email! }); }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                        title="View customer"
                                      >
                                        {audit.parent_email}
                                      </button>
                                    ) : (
                                      audit.parent_email
                                    )}
                                    &gt;
                                  </div>
                                )}
                                {audit.student_id && (
                                  <div>
                                    <span className="text-gray-400">student:</span> {audit.student_id}
                                  </div>
                                )}
                                {audit.classroom_id && (
                                  <div>
                                    <span className="text-gray-400">classroom:</span>{' '}
                                    {onOptionClick ? (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onOptionClick('Classrooms', { classroomId: audit.classroom_id! }); }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                        title="View classroom"
                                      >
                                        {audit.classroom_id}
                                      </button>
                                    ) : (
                                      audit.classroom_id
                                    )}
                                  </div>
                                )}
                                {audit.project_id && (
                                  <div>
                                    <span className="text-gray-400">project:</span> {audit.project_id}
                                  </div>
                                )}
                              </div>
                            </details>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        {/* Resent */}
                        <td className="text-left py-2 px-4 border-b text-xs whitespace-nowrap">
                          {audit.resent_at ? (
                            <span
                              className="text-green-700"
                              title={
                                audit.resent_count && audit.resent_count > 1
                                  ? `Resent ${audit.resent_count} times`
                                  : 'Resent once'
                              }
                            >
                              ✓ {formatDate(audit.resent_at)}
                              {audit.resent_count && audit.resent_count > 1 ? ` (×${audit.resent_count})` : ''}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        {/* Resend */}
                        <td className="text-left py-2 px-3 border-b">
                          {(() => {
                            const status = resendStatus[audit.id] || 'idle';
                            if (status === 'loading') {
                              return (
                                <span className="text-xs text-gray-400 whitespace-nowrap">sending…</span>
                              );
                            }
                            if (status === 'success') {
                              return (
                                <span className="text-xs text-green-600 font-medium whitespace-nowrap">✓ Sent</span>
                              );
                            }
                            if (status === 'error') {
                              return (
                                <button
                                  onClick={() => handleResend(audit.id)}
                                  disabled={bulkRunning}
                                  className="text-xs text-red-600 hover:text-red-800 font-medium whitespace-nowrap disabled:opacity-50"
                                  title="Failed — click to retry"
                                >
                                  ✗ Retry
                                </button>
                              );
                            }
                            return (
                              <button
                                onClick={() => handleResend(audit.id)}
                                disabled={bulkRunning}
                                className="text-xs text-gray-400 hover:text-storyblue hover:underline whitespace-nowrap transition-colors disabled:opacity-50"
                                title="Re-send this webhook to the marketing API"
                              >
                                ↻ Resend
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <NoRecordFound colSpan={9} />
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-4">
            <TableInfoBar
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              totalPages={totalPages}
              totalRecords={totalRecords}
              recordsOnPage={audits.length}
              label="events"
            />
            {totalPages > 1 && (
              <PaginationButtons
                totalPages={totalPages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingAudit;
